import axios from 'axios'
import tokenService from './token'
import { createApiUrl } from '../config/api'

const findUrl = createApiUrl('/api/automation/reconcile/find/')
const remediateUrl = createApiUrl('/api/automation/reconcile/remediate/')
const sourcesUrl = createApiUrl('/api/automation/sources/')
const settingsUrl = createApiUrl('/api/automation/settings/')
const statusUrl = createApiUrl('/api/automation/reconcile/status/')
const violationsUrl = createApiUrl('/api/automation/violations/')
const violationIgnoreUrl = (id) => createApiUrl(`/api/automation/violations/${id}/ignore/`)
const violationRemediateUrl = (id) => createApiUrl(`/api/automation/violations/${id}/remediate/`)
const proposalsUrl = createApiUrl('/api/automation/proposals/')
const proposalActionUrl = (id, action) => createApiUrl(`/api/automation/proposals/${id}/${action}/`)
const proposalUrl = (id) => createApiUrl(`/api/automation/proposals/${id}/`)
const proposalPrimaryUrl = (id) => createApiUrl(`/api/automation/proposals/${id}/primary/`)

// Start the find pass (detect violations). Resolves with { id, phase, state } (202) or throws on
// 409 if a run is already in progress.
const triggerFind = async () => {
    const res = await axios.post(findUrl, {}, tokenService.getAuthHeader())
    return res.data
}

// Start the remediate pass. sources (optional array of source keys) narrows candidate
// sourcing for this run; null/undefined = all sources (the server treats an absent field
// as "all" and rejects an empty list).
const triggerRemediate = async (sources = null, llm = null) => {
    // An empty array is truthy in JS, so check length explicitly — the server 400s a
    // present-but-empty sources list, whereas an absent field means "all sources".
    const body = sources && sources.length ? { sources } : {}
    // llm picks the scoring model for this run; absent = the server's env default.
    if (llm) body.llm = llm
    const res = await axios.post(remediateUrl, body, tokenService.getAuthHeader())
    return res.data
}

// The sources payload for the remediate UI: { sources: [{ key, label, resource_type,
// supported }], features: { per_violation_remediation }, llm_models: [{ key, label }],
// llm_default: key|null } — server-driven feature flags and the curated model choices
// ride on the page's once-per-load bootstrap call. `supported` is advisory: the picker
// marks a false source "Experimental", but every source stays selectable and runnable.
const getSources = async () => {
    const res = await axios.get(sourcesUrl, tokenService.getAuthHeader())
    return res.data
}

// The Configuration tab's bootstrap payload: { expiry, coverage, citation, caps }.
const getSettings = async () => {
    const res = await axios.get(settingsUrl, tokenService.getAuthHeader())
    return res.data
}

// Save the Configuration tab's form (same shape as getSettings). Returns the
// server-persisted payload; throws with a 400 body on validation failure.
const updateSettings = async (settings) => {
    const res = await axios.put(settingsUrl, settings, tokenService.getAuthHeader())
    return res.data
}

// Latest run for a phase ('find' | 'remediate'): { phase, state: 'idle' } or the full run record.
const getStatus = async (phase) => {
    const res = await axios.get(`${statusUrl}?phase=${phase}`, tokenService.getAuthHeader())
    return res.data
}

// The list endpoints' filter params — shared with the filter UI so the two can't drift.
export const LIST_FILTER_KEYS = ['constraint', 'kind', 'resourcetype', 'search']

// Query string for a paginated list: page plus any non-empty filter params.
const listQuery = (page, filters = {}) => {
    const params = new URLSearchParams({ page })
    for (const key of LIST_FILTER_KEYS) {
        if (filters[key]) params.append(key, filters[key])
    }
    return params.toString()
}

// Pending violations, paginated: { count, next, previous, total_pages, current_page, results }.
const getViolations = async (page = 1, filters = {}) => {
    const res = await axios.get(
        `${violationsUrl}?${listQuery(page, filters)}`, tokenService.getAuthHeader(),
    )
    return res.data
}

// Veto a pending violation before any proposal exists — suppressed in future find passes.
const ignoreViolation = async (id) => {
    const res = await axios.post(violationIgnoreUrl(id), {}, tokenService.getAuthHeader())
    return res.data
}

// Start a remediate run scoped to ONE violation (server-side feature flag; 404 when
// off). sources and llm narrow the run exactly like triggerRemediate.
const remediateViolation = async (id, sources = null, llm = null) => {
    const body = sources && sources.length ? { sources } : {}
    if (llm) body.llm = llm
    const res = await axios.post(violationRemediateUrl(id), body, tokenService.getAuthHeader())
    return res.data
}

// Pending proposals, paginated (same envelope as getViolations).
const getProposals = async (page = 1, filters = {}) => {
    const res = await axios.get(
        `${proposalsUrl}?${listQuery(page, filters)}`, tokenService.getAuthHeader(),
    )
    return res.data
}

// Edit a pending proposal's candidate metadata before approving. Returns the updated proposal.
// 400 = the edit doesn't validate as a resource; 409 = not pending / a REMOVE.
const updateProposal = async (id, proposedData) => {
    const res = await axios.patch(
        proposalUrl(id),
        { proposed_data: proposedData },
        tokenService.getAuthHeader()
    )
    return res.data
}

// Swap which stored candidate a pending proposal proposes. Returns the updated proposal
// (new proposed_data/fit_score/rationale/primary_candidate). 404 = candidate not on this
// proposal; 409 = not pending.
const setPrimaryCandidate = async (id, candidateId) => {
    const res = await axios.post(
        proposalPrimaryUrl(id),
        { candidate: candidateId },
        tokenService.getAuthHeader()
    )
    return res.data
}

const approveProposal = async (id) => {
    const res = await axios.post(proposalActionUrl(id, 'approve'), {}, tokenService.getAuthHeader())
    return res.data
}

const rejectProposal = async (id) => {
    const res = await axios.post(proposalActionUrl(id, 'reject'), {}, tokenService.getAuthHeader())
    return res.data
}

const dismissProposal = async (id) => {
    const res = await axios.post(proposalActionUrl(id, 'dismiss'), {}, tokenService.getAuthHeader())
    return res.data
}

const automationService = {
    triggerFind,
    triggerRemediate,
    getSources,
    getSettings,
    updateSettings,
    getStatus,
    getViolations,
    ignoreViolation,
    remediateViolation,
    getProposals,
    updateProposal,
    setPrimaryCandidate,
    approveProposal,
    rejectProposal,
    dismissProposal,
}

export default automationService
